// The app runtime: every long-lived object of the back-office, created once at startup
// (main.tsx) or per test. Providers read it from context; nothing here renders.
import { configureApiClient } from '@batiflow/api-client';
import {
  ApiError,
  createApiFetcher,
  createIdempotencyKeyFactory,
  pendingActionRequest,
  savePendingAction,
  systemClock,
  takePendingAction,
  type ApiFetcher,
  type ApiRequest,
  type FetchLike,
  type StoragePort,
} from '@batiflow/client-core';
import { isSafePlatformReturnTo, PlatformLogoutResponseSchema } from '@batiflow/contracts';
import { createI18n } from '@batiflow/i18n';
import { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { i18n as I18nInstance } from 'i18next';
import { createBrowserStorage } from '@/shared/storage';
import { createLocaleStore, type LocaleStore } from './locale-store';
import { createAdminSessionStore, selectCsrfToken, type AdminSessionStore } from './session-store';

/** Full-page navigation, injected so tests can observe redirects (jsdom cannot navigate). */
export interface AppNavigation {
  assign: (url: string) => void;
  /** Current path with its query string, e.g. `/organizations?x=1`. */
  currentPath: () => string;
}

/** What happened to the action that was waiting for a step-up. */
export type PendingActionOutcome = 'none' | 'replayed' | 'failed' | 'discarded';

/** The server-handled sign-in and step-up addresses of the back-office BFF. */
export const PLATFORM_LOGIN_PATH = '/api/v1/platform/auth/login';
export const PLATFORM_STEP_UP_PATH = '/api/v1/platform/auth/step-up';
/** The back-office path itself (the BFF accepts its pages, isSafePlatformReturnTo), else "/". */
const backofficeReturnTo = (path: string) => (isSafePlatformReturnTo(path) ? path : '/');
export const platformLoginUrl = (returnTo: string): string =>
  `${PLATFORM_LOGIN_PATH}?returnTo=${encodeURIComponent(backofficeReturnTo(returnTo))}`;
export const platformStepUpUrl = (returnTo: string): string =>
  `${PLATFORM_STEP_UP_PATH}?returnTo=${encodeURIComponent(backofficeReturnTo(returnTo))}`;

/**
 * The page the admin was on is also kept in this tab's session storage while Keycloak is open: a
 * return to "/" (an address the BFF does not accept) still lands on it through the home route.
 */
export const RETURN_PATH_KEY = 'bf.admin.returnTo';
const RETURN_PATH_TTL_MS = 10 * 60 * 1000;
const APP_ROOTS: readonly string[] = [
  'organizations',
  'break-glass',
  'jobs',
  'security',
  'audit',
  'plans',
];
const UNSAFE_CHARACTERS = /[\s\\]/;

/** A page of the back-office (never an external address, never another path). */
export function isAppPath(path: string): boolean {
  if (path.length > 1000 || !path.startsWith('/') || path.startsWith('//')) return false;
  if (UNSAFE_CHARACTERS.test(path)) return false;
  const root = path.slice(1).split(/[/?#]/, 1)[0] ?? '';
  return APP_ROOTS.includes(root);
}

/** A 403 STEP_UP_REQUIRED: the refused request is given so the action can be replayed. */
export interface StepUpBus {
  emit: (request: ApiRequest) => void;
  on: (listener: (request: ApiRequest) => void) => () => void;
}

export interface AppRuntime {
  storage: StoragePort;
  /** This tab only (sessionStorage): the action waiting for a step-up. */
  sessionStorage: StoragePort;
  localeStore: LocaleStore;
  i18n: I18nInstance;
  fetcher: ApiFetcher;
  sessionStore: AdminSessionStore;
  queryClient: QueryClient;
  stepUpBus: StepUpBus;
  navigation: AppNavigation;
  /** The page the admin was on before leaving for Keycloak (once), if it is still fresh. */
  takeReturnPath: () => string | null;
  /** Clears the session and goes to the sign-in (once, however many calls report a 401). */
  redirectToLogin: () => void;
  /**
   * Ends the server session, then goes to Keycloak's end-session page, which comes back to
   * /signed-out. Resolves false when the server did not confirm: the caller must tell the admin.
   */
  signOut: () => Promise<boolean>;
  /** Remembers `request` (refused with STEP_UP_REQUIRED) and goes to the step-up. */
  startStepUp: (request: ApiRequest) => boolean;
  /** Replays the action saved by startStepUp, once; discards it when the step-up failed. */
  replayPendingAction: (stepUpFailed: boolean) => Promise<PendingActionOutcome>;
}

export interface RuntimeOptions {
  fetch?: FetchLike;
  storage?: StoragePort;
  sessionStorage?: StoragePort;
  navigation?: AppNavigation;
}

const browserNavigation: AppNavigation = {
  assign: (url) => {
    window.location.assign(url);
  },
  currentPath: () => window.location.pathname + window.location.search,
};

const browserFetch: FetchLike = (url, init) => window.fetch(url, init as RequestInit);

/** TanStack Query defaults (docs/plan/12-frontend.md section 3). */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        // Retry twice, but never on a client error: repeating a 4xx cannot make it succeed.
        retry: (failureCount, error) =>
          failureCount < 2 &&
          !(error instanceof ApiError && error.status >= 400 && error.status < 500),
      },
    },
  });
}

function createStepUpBus(): StepUpBus {
  const listeners = new Set<(request: ApiRequest) => void>();
  return {
    emit(request) {
      for (const listener of listeners) listener(request);
    },
    on(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function createAppRuntime(options: RuntimeOptions = {}): AppRuntime {
  const storage = options.storage ?? createBrowserStorage();
  const sessionStorage = options.sessionStorage ?? createBrowserStorage('session');
  const navigation = options.navigation ?? browserNavigation;
  const fetchImpl = options.fetch ?? browserFetch;

  const localeStore = createLocaleStore(storage);
  const i18n = createI18n({
    locale: localeStore.getLocale(),
    ...(import.meta.env.DEV
      ? {
          onMissingKey: (_lng: readonly string[], ns: string, key: string) => {
            console.warn(`[i18n] missing key ${ns}:${key}`);
          },
        }
      : {}),
  });
  localeStore.subscribe(() => {
    void i18n.changeLanguage(localeStore.getLocale());
  });

  const queryClient = createAppQueryClient();
  const stepUpBus = createStepUpBus();
  const holder: { session?: AdminSessionStore } = {};
  // Set once the page is navigating away (sign-in or sign-out): later 401s must not start a
  // second navigation that would override the first one.
  let leaving = false;

  const rememberReturnPath = () => {
    const path = navigation.currentPath();
    if (!isAppPath(path)) return;
    sessionStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, at: systemClock.now() }));
  };

  const takeReturnPath = (): string | null => {
    const raw = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    if (!raw) return null;
    try {
      const saved = z.object({ path: z.string(), at: z.number() }).parse(JSON.parse(raw));
      return isAppPath(saved.path) && systemClock.now() - saved.at < RETURN_PATH_TTL_MS
        ? saved.path
        : null;
    } catch {
      return null;
    }
  };

  const redirectToLogin = () => {
    if (leaving) return;
    leaving = true;
    holder.session?.clear();
    queryClient.clear();
    rememberReturnPath();
    navigation.assign(platformLoginUrl(navigation.currentPath()));
  };

  const fetcher = createApiFetcher({
    fetch: fetchImpl,
    getLocale: () => localeStore.getLocale(),
    getCsrfToken: () => (holder.session ? selectCsrfToken(holder.session.getState()) : null),
    randomId: () => crypto.randomUUID(),
    handlers: {
      onUnauthorized: redirectToLogin,
      onStepUpRequired: (_error, request) => {
        stepUpBus.emit(request);
      },
    },
  });
  configureApiClient(fetcher);

  const sessionStore = createAdminSessionStore({
    loadMe: async () => {
      try {
        // No global handlers: a 401 here means "not signed in"; protected routes redirect through
        // AuthGate, the public page (/signed-out) must not.
        const response = await fetcher<unknown>({
          method: 'GET',
          url: '/api/v1/platform/me',
          skipHandlers: true,
        });
        return response.data;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
  holder.session = sessionStore;

  const signOut = async (): Promise<boolean> => {
    let endSessionUrl: string;
    try {
      const response = await fetcher<unknown>({
        method: 'POST',
        url: '/api/v1/platform/auth/logout',
      });
      endSessionUrl = PlatformLogoutResponseSchema.parse(response.data).endSessionUrl;
    } catch {
      return false;
    }
    leaving = true;
    sessionStore.clear();
    queryClient.clear();
    // Keycloak ends its own session, then comes back to /signed-out (post_logout_redirect_uri).
    navigation.assign(endSessionUrl);
    return true;
  };

  const newIdempotencyKey = createIdempotencyKeyFactory(systemClock, (length) =>
    crypto.getRandomValues(new Uint8Array(length)),
  );

  const startStepUp = (request: ApiRequest): boolean => {
    const saved = savePendingAction(sessionStorage, systemClock, request, newIdempotencyKey);
    rememberReturnPath();
    navigation.assign(platformStepUpUrl(navigation.currentPath()));
    return saved;
  };

  const replayPendingAction = async (stepUpFailed: boolean): Promise<PendingActionOutcome> => {
    const action = takePendingAction(sessionStorage, systemClock);
    if (!action) return 'none';
    if (stepUpFailed) return 'discarded';
    try {
      await fetcher(pendingActionRequest(action));
      await queryClient.invalidateQueries();
      return 'replayed';
    } catch {
      return 'failed';
    }
  };

  return {
    storage,
    sessionStorage,
    localeStore,
    i18n,
    fetcher,
    sessionStore,
    queryClient,
    stepUpBus,
    navigation,
    takeReturnPath,
    redirectToLogin,
    signOut,
    startStepUp,
    replayPendingAction,
  };
}
